import { useEffect, useState, useMemo, useContext } from 'react'
import {
  Button,
  ExpandableSection,
  Input,
  Grid,
  Layout,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import { DatePicker } from '@enterprise-ui/canvas-ui-react-datepicker'
import { AutocompleteFilter } from '../../globalComponents/AutocompleteFilter'
import { TextFilter } from '../../globalComponents/TextFilter'
import {
  TOASTER_DEFAULTS,
  COST_SEARCH_SEARCH_CRITERIA_FIELDS,
  COST_SEARCH_VIEW_BY_OPTIONS,
  COST_SEARCH_VIEW_BY_OPTIONS_DVS,
  COST_SEARCH_TIMEFRAME_OPTIONS,
  GET_COST_SEARCH_DEFAULT_SELECTIONS,
  getCurrentUTCDateString,
  getCurrentLocalDateString,
  convertDateToUTCDateString,
  convertDateToLocalDateStringWithBackendFormatting,
  CLG_OPTIONS_TO_SHOW,
} from '../../globalConfig/constants'
import {
  getClassOptions,
  getCustomLocationGroupOptions,
} from '../../services/requests'
import {
  processList,
  getOneDayInTheFuture,
} from '../../services/helperFunctions'
import { AppliedFilter } from '../../globalComponents/AppliedFilters'
import EnterpriseIcon, { HelpIcon, CautionIcon } from '@enterprise-ui/icons'
import { FutureDateTimezoneMismatchExplanationModal } from '../../globalComponents/FutureDateTimezoneMismatchExplanationModal'
import { ViewByModal } from './ViewByModal'
import { UnrecognizedInputsModal } from '../../globalComponents/UnrecognizedInputsModal'
import isEqual from 'lodash/isEqual'
import { VendorContext } from '../../globalState/Vendor/index'

export const CostSearch = ({
  addNewSelections,
  onSearchClick,
  onResetSelectionsClick,
  premadeSelections,
  activeSearchSelections,
  vendorId,
  removeValue,
  isExpanded,
  loading,
}) => {
  const ALL_COST_SEARCH_VIEW_BY_OPTIONS = {
    ...COST_SEARCH_VIEW_BY_OPTIONS,
    ...COST_SEARCH_VIEW_BY_OPTIONS_DVS,
  }
  const makeToast = useToaster()

  const [vendorState] = useContext(VendorContext)
  const dvsVendorState = vendorState?.dvsStatus?.[vendorState?.selectedVendor]

  const constructInitialValueStates = (dvsVendorState) => {
    const returnObject = {}
    Object.values(COST_SEARCH_SEARCH_CRITERIA_FIELDS).forEach((field) => {
      returnObject[field.variableName] =
        field.filterType === 'AUTOCOMPLETE' ? [] : ''
    })
    returnObject.viewByRadioField =
      dvsVendorState === true
        ? COST_SEARCH_VIEW_BY_OPTIONS_DVS.SUPPLIER_CHAIN.variableName
        : COST_SEARCH_VIEW_BY_OPTIONS.CHAIN.variableName
    returnObject.viewByRadioValues = []
    returnObject.timeframeRadioField = 'CURRENT'
    returnObject.timeframeRadioValue = null
    returnObject[
      COST_SEARCH_SEARCH_CRITERIA_FIELDS.HISTORICAL_ITEMS?.variableName
    ] = false
    return returnObject
  }

  const constructInitialRadioOptions = () => {
    const returnArray = []
    Object.values(COST_SEARCH_VIEW_BY_OPTIONS).forEach((field) => {
      returnArray.push({ value: field.keyName, label: field?.label })
    })
    return returnArray
  }

  const constructDVSRadioOptions = () => {
    const returnArray = []
    Object.values(COST_SEARCH_VIEW_BY_OPTIONS_DVS).forEach((field) => {
      returnArray.push({ value: field.keyName, label: field?.label })
    })
    return returnArray
  }

  const initialValueStates = useMemo(
    () => constructInitialValueStates(dvsVendorState),
    [dvsVendorState],
  )

  const [valueStates, setValueStates] = useState(initialValueStates)
  const [errorStates, setErrorStates] = useState({})
  const [globalStateSetToValueStates, setGlobalStateSetToValueStates] =
    useState(false)
  const [classOptions, setClassOptions] = useState([])
  const [customCLGOptions, setCustomCLGOptions] = useState([])
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [isViewByModalVisible, setIsViewByModalVisible] = useState(false)
  const [isTimezoneMismatchModalVisible, setIsTimezoneMismatchModalVisible] =
    useState(false)
  const [expandViewBy, setExpandViewBy] = useState(isExpanded)
  const [clgOptionsToShow, setClgOptionsToShow] = useState(
    CLG_OPTIONS_TO_SHOW.pending,
  )
  const radioOptions = constructInitialRadioOptions()
  const dvsRadioOptions = constructDVSRadioOptions()
  const departmentValue =
    valueStates[COST_SEARCH_SEARCH_CRITERIA_FIELDS.DEPARTMENTS.variableName]

  useEffect(() => {
    if (globalStateSetToValueStates) {
      if (vendorState?.dvsStatus?.[vendorId] === 'PENDING') {
        setClgOptionsToShow(CLG_OPTIONS_TO_SHOW.pending)
      } else if (vendorState?.dvsStatus?.[vendorId] === true) {
        setClgOptionsToShow(CLG_OPTIONS_TO_SHOW.dvs)
      } else {
        setClgOptionsToShow(CLG_OPTIONS_TO_SHOW.standard)
      }
    }
  }, [vendorState, vendorId, globalStateSetToValueStates])

  useEffect(() => {
    const isDvs = clgOptionsToShow === CLG_OPTIONS_TO_SHOW.dvs

    const defaultViewBySelection = isDvs
      ? COST_SEARCH_VIEW_BY_OPTIONS_DVS.SUPPLIER_CHAIN
      : COST_SEARCH_VIEW_BY_OPTIONS.CHAIN

    const switchingToStandard =
      !isDvs &&
      Object.keys(COST_SEARCH_VIEW_BY_OPTIONS_DVS).includes(
        valueStates.viewByRadioField,
      )

    const switchingToDvs =
      isDvs &&
      Object.keys(COST_SEARCH_VIEW_BY_OPTIONS).includes(
        valueStates.viewByRadioField,
      )

    if (switchingToStandard || switchingToDvs) {
      setValueStates((prevValueStates) => {
        const newState = {
          ...prevValueStates,
          viewByRadioField: defaultViewBySelection.variableName,
          viewByRadioValues: [],
        }
        return newState
      })

      const newSelections = {
        costLocationGroup: {
          costLocationGroupType: defaultViewBySelection.variableName,
          costLocationGroupValues: [],
        },
      }
      addNewSelections(newSelections)

      const hadLocationValues = valueStates.viewByRadioValues?.length > 0

      if (switchingToStandard && hadLocationValues) {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'info',
          heading: 'Info',
          message:
            'We have removed your location level filters because you are no longer searching for cost with a DVS vendor. Select new location filters if necessary.',
        })
      } else if (switchingToDvs && hadLocationValues) {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'info',
          heading: 'Info',
          message:
            'We have removed your location level filters because you are now searching with DVS vendors. Select new supplier location filters if necessary.',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clgOptionsToShow])

  useEffect(() => {
    if (departmentValue.length === 1) {
      getClassOptions(departmentValue[0].value)
        .then((response) => {
          if (response?.status === 200) {
            setClassOptions(response.data)
          }
        })
        .catch(() => {})
    } else {
      setClassOptions([])
      setValueStates((prevValueStates) => {
        return {
          ...prevValueStates,
          [COST_SEARCH_SEARCH_CRITERIA_FIELDS.DEPARTMENT_CLASS.variableName]:
            [],
        }
      })
    }
  }, [departmentValue])

  useEffect(() => {
    if (vendorId) {
      getCustomLocationGroupOptions(vendorId)
        .then((response) => {
          if (response?.status === 200) {
            setCustomCLGOptions(response.data)
          }
        })
        .catch(() => {})
    }
    setCustomCLGOptions([])
    if (
      (ALL_COST_SEARCH_VIEW_BY_OPTIONS.CUSTOM?.keyName &&
        valueStates.viewByRadioField ===
          ALL_COST_SEARCH_VIEW_BY_OPTIONS.CUSTOM.keyName) ||
      (ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_LOCATION?.keyName &&
        valueStates.viewByRadioField ===
          ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_LOCATION.keyName)
    ) {
      viewByAutocompleteSetter([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId])

  useEffect(() => {
    if (Object.keys(errorStates).length) {
      setIsErrorModalVisible(true)
    }
  }, [errorStates])

  useEffect(() => {
    if (premadeSelections) {
      let isGivenDateInThePast = false
      if (
        new Date() >
        new Date(
          premadeSelections[
            COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName
          ],
        )
      ) {
        isGivenDateInThePast = true
        // trigger a popup that explains that the future date saved to the
        // view is now in the past so you'll need to pick a new date or
        // modify the timeframe selection to proceed
      }
      let convertedDate = premadeSelections[
        COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName
      ]
        ? convertDateToUTCDateString(
            new Date(
              premadeSelections[
                COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName
              ],
            ),
          )
        : null
      setValueStates((prevValueStates) => {
        return {
          ...prevValueStates,
          viewByRadioField:
            premadeSelections?.costLocationGroup?.costLocationGroupType,
          viewByRadioValues:
            premadeSelections?.costLocationGroup?.costLocationGroupValues?.map(
              (value) => {
                return { id: value, label: value, value: value }
              },
            ),
          timeframeRadioField:
            premadeSelections[
              COST_SEARCH_TIMEFRAME_OPTIONS.TIMEFRAME.variableName
            ],
          timeframeRadioValue: isGivenDateInThePast ? null : convertedDate,
        }
      })
      addNewSelections({
        ...premadeSelections,
        costLocationGroup: {
          costLocationGroupType:
            premadeSelections?.costLocationGroup?.costLocationGroupType,
          costLocationGroupValues:
            premadeSelections?.costLocationGroup?.costLocationGroupValues,
        },
        [COST_SEARCH_TIMEFRAME_OPTIONS.TIMEFRAME.variableName]:
          premadeSelections[
            COST_SEARCH_TIMEFRAME_OPTIONS.TIMEFRAME.variableName
          ],
        [COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName]:
          isGivenDateInThePast
            ? null
            : premadeSelections[
                COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName
              ],
        [COST_SEARCH_TIMEFRAME_OPTIONS.END_DATE.variableName]:
          isGivenDateInThePast
            ? null
            : premadeSelections[
                COST_SEARCH_TIMEFRAME_OPTIONS.END_DATE.variableName
              ],
      })
    }
    setGlobalStateSetToValueStates(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchCriteriaSetter = (field, inputValue) => {
    setValueStates({
      ...valueStates,
      [field.variableName]:
        field?.filterType === 'AUTOCOMPLETE'
          ? inputValue
          : inputValue?.target?.value,
    })
  }

  const viewByRadioSetter = (inputValue) => {
    setValueStates({
      ...valueStates,
      viewByRadioField: inputValue,
      viewByRadioValues: [],
    })
    addNewSelections({
      costLocationGroup: {
        costLocationGroupType: inputValue,
        costLocationGroupValues: [],
      },
    })
  }

  const viewByAutocompleteSetter = (inputValue) => {
    setValueStates({
      ...valueStates,
      viewByRadioValues: inputValue,
    })
    addNewSelections({
      costLocationGroup: {
        costLocationGroupType: valueStates.viewByRadioField,
        costLocationGroupValues: inputValue.map((element) => {
          return element.value
        }),
      },
    })
  }

  const timeframeRadioSetter = (inputValue) => {
    setValueStates({
      ...valueStates,
      timeframeRadioField: inputValue,
      timeframeRadioValue: null,
    })
    addNewSelections({
      [COST_SEARCH_TIMEFRAME_OPTIONS.TIMEFRAME.variableName]: inputValue,
      [COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName]: null,
      [COST_SEARCH_TIMEFRAME_OPTIONS.END_DATE.variableName]: null,
    })
  }

  const checkboxSearchCriteriaSetter = (field, inputValue) => {
    setValueStates({
      ...valueStates,
      [field.variableName]: inputValue.target.checked,
    })
    addNewSelections({
      [field.variableName]: inputValue.target.checked,
    })
  }

  const timeframeDateSetter = (inputValue) => {
    if (inputValue) {
      const formattedDate = convertDateToLocalDateStringWithBackendFormatting(
        new Date(inputValue),
      )
      setValueStates({
        ...valueStates,
        timeframeRadioValue: inputValue,
      })
      addNewSelections({
        [COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName]: formattedDate,
        [COST_SEARCH_TIMEFRAME_OPTIONS.END_DATE.variableName]: formattedDate,
      })
    } else {
      setValueStates({
        ...valueStates,
        timeframeRadioValue: null,
      })
      addNewSelections({
        [COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName]: null,
        [COST_SEARCH_TIMEFRAME_OPTIONS.END_DATE.variableName]: null,
      })
    }
  }

  const isAddCriteriaButtonDisabled = () => {
    let hasAnInput = false
    Object.values(COST_SEARCH_SEARCH_CRITERIA_FIELDS).forEach((field) => {
      if (valueStates[field.variableName]?.length > 0) {
        hasAnInput = true
      }
    })
    return !hasAnInput
  }

  const isSearchButtonDisabled = () => {
    if (
      ALL_COST_SEARCH_VIEW_BY_OPTIONS[valueStates.viewByRadioField]
        ?.filterDetails &&
      valueStates.viewByRadioValues?.length === 0
    ) {
      return true
    } else if (
      valueStates?.timeframeRadioField === 'FUTURE' &&
      !valueStates?.timeframeRadioValue
    ) {
      return true
    } else if (loading) {
      return true
    } else {
      return false
    }
  }

  const clearViewByInputs = (value) => {
    if (value?.length) {
      let tempList = valueStates.viewByRadioValues
      let valueArray = tempList.filter((e) => e.value !== value)
      setValueStates({
        ...valueStates,
        viewByRadioValues: valueArray,
      })
    } else {
      setValueStates({
        ...valueStates,
        viewByRadioValues: [],
      })
    }
  }

  const clearSearchCriteriaInputs = () => {
    const emptyStates = {}
    Object.values(COST_SEARCH_SEARCH_CRITERIA_FIELDS).forEach((field) => {
      emptyStates[field.variableName] =
        field.filterType === 'AUTOCOMPLETE' ? [] : ''
    })
    setValueStates({
      ...valueStates,
      ...emptyStates,
    })
  }

  const handleAddCriteriaClick = (makeSearchRequestAfterAdding) => {
    const returnObject = {}
    const unrecognizedValues = {}
    Object.values(COST_SEARCH_SEARCH_CRITERIA_FIELDS).forEach((field) => {
      if (valueStates[field.variableName]?.length) {
        if (typeof valueStates[field.variableName] === 'string') {
          const processListResults = processList(
            valueStates[field.variableName],
            field?.regexPattern,
          )
          returnObject[field.variableName] =
            processListResults?.recognizedValues
          if (processListResults?.unrecognizedValues.length) {
            unrecognizedValues[field.keyName] =
              processListResults?.unrecognizedValues
          }
        } else {
          if (field.keyName === 'DEPARTMENT_CLASS') {
            returnObject[field.variableName] = valueStates[
              field.variableName
            ].map((element) => {
              return `${
                valueStates[
                  COST_SEARCH_SEARCH_CRITERIA_FIELDS.DEPARTMENTS.variableName
                ][0].value
              }-${element.value}`
            })
          } else {
            returnObject[field.variableName] = valueStates[
              field.variableName
            ].map((element) => {
              return element.value
            })
          }
        }
      }
    })
    setErrorStates(unrecognizedValues)
    addNewSelections(returnObject, makeSearchRequestAfterAdding)
    clearSearchCriteriaInputs()
  }

  const handleRemoveFromAppliedFilters = (item, field, values, type) => {
    removeValue(item, field, values, type)
    if (
      Object.values(ALL_COST_SEARCH_VIEW_BY_OPTIONS).some((fieldValue) => {
        return fieldValue.variableName === field
      })
    ) {
      clearViewByInputs(item)
    }
  }

  const handleSearchClick = () => {
    if (isAddCriteriaButtonDisabled()) {
      onSearchClick()
    } else {
      handleAddCriteriaClick(true)
    }
  }

  const handleResetSelectionsClick = () => {
    setValueStates(initialValueStates)
    onResetSelectionsClick()
  }

  const handleCloseViewByModal = () => {
    setIsViewByModalVisible(false)
  }

  const handleSelectViewByModal = (value) => {
    viewByRadioSetter(value)
    setExpandViewBy(true)
  }

  const getDisableBeforeDate = () => {
    const today = new Date()
    const tomorrow = getOneDayInTheFuture(today)
    return convertDateToUTCDateString(tomorrow)
  }

  const isDvs = clgOptionsToShow === CLG_OPTIONS_TO_SHOW.dvs
  const activeViewByOptions = isDvs
    ? COST_SEARCH_VIEW_BY_OPTIONS_DVS
    : COST_SEARCH_VIEW_BY_OPTIONS
  const activeRadioOptions = isDvs ? dvsRadioOptions : radioOptions

  return (
    <>
      <Layout.Body
        className="hesp-costSearch-layout"
        data-testid="cost-search-body"
      >
        <Grid.Container className="hc-pa-normal">
          <Grid.Item className="hesp-general-fullwidth hc-pa-none">
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={isExpanded}
            >
              <p className="hc-fs-md">
                <strong>Search Criteria</strong>
              </p>
              <ExpandableSection.Content>
                <div
                  style={{ columnGap: '24px' }}
                  className="hesp-general-flex hesp-general-flex-wrap hesp-general-alignItemsCenter hc-pl-lg hc-pt-xs"
                >
                  {Object.values(COST_SEARCH_SEARCH_CRITERIA_FIELDS).map(
                    (field) => (
                      <div
                        key={`${field.variableName}_container`}
                        className="hesp-general-scContainer"
                      >
                        {field?.filterType === 'AUTOCOMPLETE' ? (
                          <AutocompleteFilter
                            id={`${field.variableName}_autocomplete`}
                            label={field?.label}
                            fieldName={
                              field?.filterDetails?.autocompleteRequestName
                            }
                            optionsRequest={
                              field?.keyName === 'DEPARTMENT_CLASS'
                                ? classOptions
                                : field?.filterDetails?.autocompleteRequestFunc
                            }
                            sortOptions={field?.filterDetails?.sortFunc}
                            selectedOptions={(input) =>
                              searchCriteriaSetter(field, input)
                            }
                            requestType={field?.filterDetails?.requestType}
                            data-testid={`${field.variableName}_autocomplete`}
                            multiselect
                            optionHeight="expanded"
                            value={valueStates[field.variableName]}
                            disabled={
                              field?.keyName === 'DEPARTMENT_CLASS'
                                ? valueStates[
                                    COST_SEARCH_SEARCH_CRITERIA_FIELDS
                                      .DEPARTMENTS.variableName
                                  ].length !== 1
                                : null
                            }
                          />
                        ) : field?.filterType === 'TEXT' ? (
                          <TextFilter
                            id={`${field.variableName}_text_filter`}
                            label={field.label}
                            data-testid={`${field.variableName}_text_filter`}
                            onChange={(input) =>
                              searchCriteriaSetter(field, input)
                            }
                            value={valueStates?.[field.variableName]}
                          />
                        ) : null}
                      </div>
                    ),
                  )}
                  <Input.Checkbox
                    id={`${COST_SEARCH_SEARCH_CRITERIA_FIELDS['HISTORICAL_ITEMS'].variableName}_checkbox`}
                    label={`${COST_SEARCH_SEARCH_CRITERIA_FIELDS['HISTORICAL_ITEMS']?.label}`}
                    onChange={(event) => {
                      checkboxSearchCriteriaSetter(
                        COST_SEARCH_SEARCH_CRITERIA_FIELDS['HISTORICAL_ITEMS'],
                        event,
                      )
                    }}
                    checked={
                      valueStates[
                        COST_SEARCH_SEARCH_CRITERIA_FIELDS['HISTORICAL_ITEMS']
                          .variableName
                      ]
                    }
                  />
                  <div
                    style={{ flexGrow: 1, height: '50px' }}
                    className="hesp-general-flex hesp-general-justifyEnd hesp-general-alignItemsCenter hc-pr-xs"
                  >
                    <Button
                      type="secondary"
                      onClick={(e) => handleAddCriteriaClick()}
                      disabled={isAddCriteriaButtonDisabled()}
                      data-testid="add_criteria_button"
                    >
                      ADD CRITERIA
                    </Button>
                  </div>
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              onExpand={() => setExpandViewBy(!expandViewBy)}
              expanded={expandViewBy}
            >
              <div className="hc-fs-md">
                <strong>View By</strong>
                <EnterpriseIcon
                  data-testid="view-by-help"
                  className="hesp-general-marginLeft"
                  style={{ paddingLeft: '5px' }}
                  icon={HelpIcon}
                  size="sm"
                  onClick={(e) => {
                    setIsViewByModalVisible(true)
                    e.stopPropagation()
                  }}
                />
              </div>
              <ExpandableSection.Content>
                <Input.Radio
                  className="hesp-radio-label"
                  id="view_by_radio"
                  data-testid="view_by_radio"
                  options={activeRadioOptions}
                  onUpdate={(id, value) => {
                    viewByRadioSetter(value)
                  }}
                  value={valueStates.viewByRadioField}
                  disabled={clgOptionsToShow === CLG_OPTIONS_TO_SHOW.pending}
                />
                {activeViewByOptions[valueStates.viewByRadioField]
                  ?.filterType === 'AUTOCOMPLETE' ? (
                  <div
                    className="hc-pt-dense hc-ml-lg"
                    style={{ width: 'calc(100% - 48px)', minWidth: '175px' }}
                  >
                    <AutocompleteFilter
                      key={
                        activeViewByOptions[valueStates.viewByRadioField]
                          ?.keyName === 'SUPPLIER_LOCATION'
                          ? `${activeViewByOptions[valueStates.viewByRadioField].variableName}_${vendorId}`
                          : activeViewByOptions[valueStates.viewByRadioField]
                              .variableName
                      }
                      id={`${
                        activeViewByOptions[valueStates.viewByRadioField]
                          .variableName
                      }_autocomplete`}
                      label={
                        activeViewByOptions[valueStates.viewByRadioField].label
                      }
                      fieldName={
                        activeViewByOptions[valueStates.viewByRadioField]
                          ?.filterDetails?.autocompleteRequestName
                      }
                      optionsRequest={
                        valueStates.viewByRadioField ===
                        COST_SEARCH_VIEW_BY_OPTIONS.CUSTOM?.keyName
                          ? customCLGOptions
                          : activeViewByOptions[valueStates.viewByRadioField]
                                ?.keyName === 'SUPPLIER_LOCATION'
                            ? () => {
                                if (!vendorId) {
                                  return Promise.resolve({ data: [] })
                                }
                                return activeViewByOptions[
                                  valueStates.viewByRadioField
                                ]?.filterDetails?.autocompleteRequestFunc(
                                  vendorId,
                                )
                              }
                            : activeViewByOptions[valueStates.viewByRadioField]
                                ?.filterDetails?.autocompleteRequestFunc
                      }
                      selectedOptions={(input) =>
                        viewByAutocompleteSetter(input)
                      }
                      requestType={
                        activeViewByOptions[valueStates.viewByRadioField]
                          ?.filterDetails?.requestType
                      }
                      data-testid={`${
                        activeViewByOptions[valueStates.viewByRadioField]
                          .variableName
                      }_autocomplete`}
                      multiselect
                      required
                      value={valueStates?.viewByRadioValues}
                    />
                  </div>
                ) : null}
              </ExpandableSection.Content>
            </ExpandableSection>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={isExpanded}
            >
              <p className="hc-fs-md">
                <strong>Timeframe</strong>
              </p>
              <ExpandableSection.Content>
                <Input.Radio
                  className="hesp-radio-label hesp-general-inline"
                  id="timeframe_radio"
                  data-testid="timeframe_radio"
                  options={[
                    { value: 'CURRENT', label: 'Current Costs' },
                    { value: 'FUTURE', label: 'Future Costs' },
                  ]}
                  onUpdate={(id, value) => {
                    timeframeRadioSetter(value)
                  }}
                  value={valueStates.timeframeRadioField}
                />
                <div
                  className="hc-ml-normal"
                  style={{
                    width: '240px',
                    display: 'inline-block',
                  }}
                >
                  <DatePicker
                    className="hesp-costSearch-futureDatePicker"
                    id="timeframe_datepicker"
                    label="Effective on:"
                    data-testid="FUTURE_datepicker"
                    onUpdate={(id, value) => {
                      timeframeDateSetter(value)
                    }}
                    disableDates={{ before: getDisableBeforeDate() }}
                    value={valueStates.timeframeRadioValue}
                    disabled={valueStates.timeframeRadioField !== 'FUTURE'}
                    required
                  />
                  {getCurrentUTCDateString() !== getCurrentLocalDateString() ? (
                    <Button
                      className="hesp-costSearch-thresholdAlert"
                      data-testid="startDate-timezone-mismatch-warning"
                      iconOnly
                      type="ghost"
                      aria-label="Timezone Mismatch Warning Icon"
                      onClick={() => {
                        setIsTimezoneMismatchModalVisible(true)
                      }}
                    >
                      <EnterpriseIcon icon={CautionIcon} />
                    </Button>
                  ) : null}
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <div className="hc-pt-normal hesp-general-flex hesp-general-justifyEnd">
              <Button
                className="hc-mr-normal"
                type="secondary"
                onClick={handleResetSelectionsClick}
                disabled={
                  isEqual(
                    activeSearchSelections,
                    GET_COST_SEARCH_DEFAULT_SELECTIONS(dvsVendorState === true),
                  ) && isEqual(initialValueStates, valueStates)
                }
                data-testid="reset_selections_button"
              >
                RESET SELECTIONS
              </Button>
              <Button
                type="primary"
                onClick={handleSearchClick}
                disabled={isSearchButtonDisabled()}
                data-testid="search_button"
              >
                SEARCH
              </Button>
            </div>
            <UnrecognizedInputsModal
              isVisible={isErrorModalVisible}
              onRefuse={() => setIsErrorModalVisible(false)}
              errorStates={errorStates}
              possibleFields={COST_SEARCH_SEARCH_CRITERIA_FIELDS}
            />
          </Grid.Item>
        </Grid.Container>
      </Layout.Body>
      <Layout.Rail data-testid="cost-search-rail">
        <AppliedFilter
          selectedValues={activeSearchSelections}
          removeValue={handleRemoveFromAppliedFilters}
        ></AppliedFilter>
      </Layout.Rail>
      <ViewByModal
        showModal={isViewByModalVisible}
        onClose={handleCloseViewByModal}
        onSelect={handleSelectViewByModal}
      />
      <FutureDateTimezoneMismatchExplanationModal
        isVisible={isTimezoneMismatchModalVisible}
        onRefuse={() => {
          setIsTimezoneMismatchModalVisible(false)
        }}
      />
    </>
  )
}
